import { useState, useEffect } from 'react';

export default function Booking() {
  const [formData, setFormData] = useState({
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    childName: '',
    childAge: '',
    childGrade: '',
    concerns: '',
    previousAssessments: '',
    preferredDate: '',
    preferredTime: '',
    additionalInfo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Add Schema.org JSON-LD for booking page
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Book Consultation - CogniHeroes Academy",
      "description": "Schedule your child's comprehensive neurofeedback assessment for R300. Our evaluation covers cognitive, emotional, and neurological patterns.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/booking`,
      "mainEntity": {
        "@type": "Service",
        "name": "Neurofeedback Consultation",
        "description": "Comprehensive cognitive assessment and personalized neurofeedback program design for children with learning differences.",
        "provider": {
          "@type": "EducationalOrganization",
          "name": "CogniHeroes Academy"
        },
        "offers": {
          "@type": "Offer",
          "price": "300",
          "priceCurrency": "ZAR",
          "description": "Initial consultation including comprehensive assessment and personalized plan"
        },
        "serviceType": "Educational Assessment",
        "areaServed": {
          "@type": "Country",
          "name": "South Africa"
        }
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Book Consultation",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/booking`
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/5aqo0ek5ed1esoiuikum', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      if (response.ok) {
        setSubmitMessage('Registration successful! We will contact you within 24 hours to confirm your consultation appointment.');
        setFormData({
          parentName: '',
          parentEmail: '',
          parentPhone: '',
          childName: '',
          childAge: '',
          childGrade: '',
          concerns: '',
          previousAssessments: '',
          preferredDate: '',
          preferredTime: '',
          additionalInfo: ''
        });
      } else {
        setSubmitMessage('There was an error submitting your registration. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('There was an error submitting your registration. Please try again.');
    }
    
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-stone-50" style={{ fontFamily: '"Inter", sans-serif' }}>
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-amber-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="https://static.readdy.ai/image/9173bb0b0f2f56000757a7cf45758267/1ea37a87bc1fe065fa3e33c05120ca7c.png" 
                alt="CogniHeroes Academy Logo" 
                className="h-10 w-10"
              />
              <a href="/" className="text-2xl font-bold text-amber-900 cursor-pointer" style={{ fontFamily: '"Playfair Display", serif' }}>
                CogniHeroes Academy
              </a>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <a href="/" className="text-amber-800 hover:text-orange-700 px-3 py-2 text-sm font-medium whitespace-nowrap cursor-pointer">Home</a>
              </div>
            </div>
            <div className="md:hidden">
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-amber-900 hover:text-orange-700 cursor-pointer"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
                </div>
              </button>
            </div>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4">
              <div className="flex flex-col space-y-2">
                <a href="/" className="text-amber-800 hover:text-orange-700 px-3 py-2 text-sm font-medium cursor-pointer">Home</a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-orange-600 via-amber-600 to-orange-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
            Book Your Consultation
          </h1>
          <p className="text-xl text-orange-100 leading-relaxed max-w-3xl mx-auto">
            Schedule your child's comprehensive assessment for R300. Our evaluation covers cognitive, emotional, and neurological patterns to design a personalized neurofeedback program.
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-lg p-8 md:p-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-amber-900 mb-4" style={{ fontFamily: '"Playfair Display", serif' }}>
                Consultation Registration
              </h2>
              <p className="text-stone-600">
                Please complete this form to register for your child's initial consultation and assessment.
              </p>
            </div>

            {submitMessage && (
              <div className={`mb-8 p-4 rounded-lg ${submitMessage.includes('successful') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {submitMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} data-readdy-form id="consultation-booking" className="space-y-8">
              {/* Parent Information */}
              <div>
                <h3 className="text-xl font-semibold text-amber-900 mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
                  Parent/Guardian Information
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="parentName" className="block text-sm font-medium text-stone-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="parentName"
                      name="parentName"
                      value={formData.parentName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="parentEmail" className="block text-sm font-medium text-stone-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="parentEmail"
                      name="parentEmail"
                      value={formData.parentEmail}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>
                <div className="mt-6">
                  <label htmlFor="parentPhone" className="block text-sm font-medium text-stone-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="parentPhone"
                    name="parentPhone"
                    value={formData.parentPhone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>

              {/* Child Information */}
              <div>
                <h3 className="text-xl font-semibold text-amber-900 mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
                  Child Information
                </h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <label htmlFor="childName" className="block text-sm font-medium text-stone-700 mb-2">
                      Child's Full Name *
                    </label>
                    <input
                      type="text"
                      id="childName"
                      name="childName"
                      value={formData.childName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="Enter child's name"
                    />
                  </div>
                  <div>
                    <label htmlFor="childAge" className="block text-sm font-medium text-stone-700 mb-2">
                      Age *
                    </label>
                    <input
                      type="number"
                      id="childAge"
                      name="childAge"
                      value={formData.childAge}
                      onChange={handleInputChange}
                      required
                      min="5"
                      max="18"
                      className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="Age"
                    />
                  </div>
                  <div>
                    <label htmlFor="childGrade" className="block text-sm font-medium text-stone-700 mb-2">
                      Current Grade *
                    </label>
                    <input
                      type="text"
                      id="childGrade"
                      name="childGrade"
                      value={formData.childGrade}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="e.g., Grade 3"
                    />
                  </div>
                </div>
              </div>

              {/* Assessment Details */}
              <div>
                <h3 className="text-xl font-semibold text-amber-900 mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
                  Assessment Information
                </h3>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="concerns" className="block text-sm font-medium text-stone-700 mb-2">
                      Primary Concerns or Areas of Focus *
                    </label>
                    <textarea
                      id="concerns"
                      name="concerns"
                      value={formData.concerns}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none"
                      placeholder="Please describe your child's learning challenges, attention difficulties, or areas where you'd like to see improvement..."
                    />
                    <p className="text-xs text-stone-500 mt-1">{formData.concerns.length}/500 characters</p>
                  </div>
                  <div>
                    <label htmlFor="previousAssessments" className="block text-sm font-medium text-stone-700 mb-2">
                      Previous Assessments or Diagnoses
                    </label>
                    <textarea
                      id="previousAssessments"
                      name="previousAssessments"
                      value={formData.previousAssessments}
                      onChange={handleInputChange}
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none"
                      placeholder="Please list any previous educational or psychological assessments, diagnoses (ADHD, autism, dyslexia, etc.), or therapies your child has received..."
                    />
                    <p className="text-xs text-stone-500 mt-1">{formData.previousAssessments.length}/500 characters</p>
                  </div>
                </div>
              </div>

              {/* Scheduling Preferences */}
              <div>
                <h3 className="text-xl font-semibold text-amber-900 mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
                  Scheduling Preferences
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="preferredDate" className="block text-sm font-medium text-stone-700 mb-2">
                      Preferred Date
                    </label>
                    <input
                      type="date"
                      id="preferredDate"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="preferredTime" className="block text-sm font-medium text-stone-700 mb-2">
                      Preferred Time
                    </label>
                    <select
                      id="preferredTime"
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="">Select preferred time</option>
                      <option value="morning">Morning (8:00 AM - 12:00 PM)</option>
                      <option value="afternoon">Afternoon (12:00 PM - 4:00 PM)</option>
                      <option value="evening">Evening (4:00 PM - 6:00 PM)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <label htmlFor="additionalInfo" className="block text-sm font-medium text-stone-700 mb-2">
                  Additional Information
                </label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none"
                  placeholder="Any additional information you'd like us to know about your child or specific questions you have..."
                />
                <p className="text-xs text-stone-500 mt-1">{formData.additionalInfo.length}/500 characters</p>
              </div>

              {/* Consultation Fee Information */}
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                <h4 className="text-lg font-semibold text-amber-900 mb-3" style={{ fontFamily: '"Playfair Display", serif' }}>
                  Consultation Fee: R300
                </h4>
                <ul className="text-sm text-stone-700 space-y-2">
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-600 mr-2 mt-0.5"></i>
                    Comprehensive cognitive assessment
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-600 mr-2 mt-0.5"></i>
                    Emotional and neurological evaluation
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-600 mr-2 mt-0.5"></i>
                    Personalized neurofeedback program design
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-orange-600 mr-2 mt-0.5"></i>
                    Detailed report with recommendations
                  </li>
                </ul>
                <p className="text-xs text-stone-600 mt-4">
                  Payment will be collected at the time of your consultation appointment.
                </p>
              </div>

              {/* Submit Button */}
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white px-12 py-4 rounded-full text-lg font-semibold whitespace-nowrap cursor-pointer transition-colors shadow-lg disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Register Now'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-amber-900 mb-8" style={{ fontFamily: '"Playfair Display", serif' }}>
            Questions? Contact Us
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-phone-line text-2xl text-orange-600"></i>
              </div>
              <p className="text-stone-700">+27 69 423 8367</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-mail-line text-2xl text-orange-600"></i>
              </div>
              <p className="text-stone-700">cogniheroesacademy@gmail.com</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-map-pin-line text-2xl text-orange-600"></i>
              </div>
              <p className="text-stone-700">West Acres, Mbombela</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-amber-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <img 
                src="https://static.readdy.ai/image/9173bb0b0f2f56000757a7cf45758267/1ea37a87bc1fe065fa3e33c05120ca7c.png" 
                alt="CogniHeroes Academy Logo" 
                className="h-8 w-8"
              />
              <h3 className="text-2xl font-bold" style={{ fontFamily: '"Playfair Display", serif' }}>
                CogniHeroes Academy
              </h3>
            </div>
            <p className="text-orange-200 mb-8">Empowering every mind through inclusive education</p>
            <div className="border-t border-orange-800 pt-8">
              <p className="text-orange-200 text-sm">
                © 2025 CogniHeroes Academy — Empowering Every Mind
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-orange-600 hover:bg-orange-700 text-white w-14 h-14 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center cursor-pointer transition-all animate-bounce z-50"
        aria-label="Back to top"
      >
        <i className="ri-arrow-up-line text-xl"></i>
      </button>
    </div>
  );
}
